/**
 * SEE-1274: applicant-facing text for the session statuses `startOnboardingSession`,
 * `bindOnboardingPhone` and `verifyOnboardingCode` return.
 *
 * Req 2.3 wants the server's actual reason shown rather than a generic failure, so every refusal
 * gets its own sentence. An unmapped status returns `null` on purpose: the caller then falls back to
 * its own wording, which keeps a status added server-side from rendering as a blank error.
 *
 * No entry here may use a directional word ("below", "above"). These strings surface in a snackbar on
 * two different screens, where nothing is reliably below anything. Directional copy belongs only in
 * `VerifyCode`, next to the layout that makes it true.
 */
import translateOrKey from 'util/translateOrKey';

const MESSAGE_KEYS: Record<string, string> = {
  INVALID_INVITATION_CODE: 'invalidInvitationCode',
  INVALID_PHONE: 'invalidPhoneNumberEntered',
  // Reached by calling `bindOnboardingPhone` on a draft that already has a number. No code has been
  // sent at that point, so the old "continue with the code we sent it" was both ungrammatical and
  // untrue — it pointed at a code that does not exist.
  PHONE_ALREADY_BOUND: 'phoneAlreadyBound',
  // TOO_SOON and CODE_STILL_VALID are deliberately absent. Both are resend-floor refusals, but they
  // are opposite instructions — "you already hold a live code, type it" versus "the last code is
  // dead, wait for a new one" — and the wording of each needs the masked number or the countdown,
  // neither of which is available here. `VerifyCode` owns both; anywhere else falls back to the
  // caller's generic message.
  HOURLY_CAP_REACHED: 'tooManyCodesThisHour',
};

/**
 * The translation key for a status, or null if we have no wording for it.
 *
 * Exported separately from the translated string so tests can assert the routing without depending
 * on copy, and so `onboardTranslationKeys` can check both locales carry every key. These used to be
 * English literals in this map: an `es` applicant entering an unknown invitation code got an English
 * sentence on a fully Spanish screen, and the existing key-parity test could not see it because it
 * only scans `t()` call sites.
 */
export const onboardSessionMessageKey = (status: string | null | undefined): string | null =>
  status ? MESSAGE_KEYS[status] ?? null : null;

/**
 * Translated at call time rather than through `useTranslation`: this is a plain module used from
 * thunk callbacks and snackbar handlers, not a component.
 *
 * `translateOrKey` rather than a bare `t` because an unresolved translation must not come back
 * empty here — the callers all write `onboardSessionMessage(status) ?? genericFailure`, so an
 * `undefined` would replace the specific reason we *do* have with "we could not start your
 * application". That is the exact regression Req 2.3 exists to prevent.
 */
export const onboardSessionMessage = (status: string | null | undefined): string | null => {
  const key = onboardSessionMessageKey(status);
  return key ? translateOrKey(key) : null;
};

export const CODE_SENT = 'CODE_SENT';
export const NEEDS_PHONE = 'NEEDS_PHONE';

/**
 * The two resend-floor refusals. Neither sends a code; they differ in whether an *earlier* code is
 * still usable, and the applicant-facing instruction is opposite in each case.
 *
 * The server owns the distinction because the client cannot derive it. On the bound path the floor
 * and the code TTL are both 300s, so elapsed time alone cannot separate "your code is still alive"
 * from "your code was consumed and you must wait" — a successful verify deletes the code without
 * releasing the floor. cdk `onboarding_session_common.py:295` picks between them on a read-only
 * liveness probe.
 *
 * This file previously mapped `TOO_SOON` to "we already sent a code, enter it" on the reasoning that
 * the floor and the TTL being equal made a live code implied. That was true only before the split,
 * and inverted after it: post-split `TOO_SOON` is returned *specifically* when no live code exists.
 * The wrong-way-round message is the thing the backend split out to fix — see the note on
 * `REFUSED_CODE_STILL_VALID` in the cdk repo.
 */
export const TOO_SOON = 'TOO_SOON';
export const CODE_STILL_VALID = 'CODE_STILL_VALID';

/**
 * `verifyOnboardingCode.reason`. `attemptsRemaining` alone cannot tell these apart — it is 0 for
 * both `EXPIRED` and `LOCKED` — which is why the earlier build said "incorrect code" for a code that
 * had simply stopped being live.
 *
 * `EXPIRED` also covers the case that prompted this: a successful verify consumes the code, so
 * returning to onboarding within the window finds no live code at all.
 */
export const VERIFY_EXPIRED = 'EXPIRED';
export const VERIFY_LOCKED = 'LOCKED';
export const VERIFY_INCORRECT = 'INCORRECT';
export const VERIFY_INVALID_SESSION = 'INVALID_SESSION';

/** Reasons where no live code exists, so retyping cannot help — only a new code will. */
export const VERIFY_NEEDS_NEW_CODE = [VERIFY_EXPIRED, VERIFY_LOCKED];
export const HOURLY_CAP_REACHED = 'HOURLY_CAP_REACHED';

/**
 * Statuses that route to the phone screen rather than showing an error.
 *
 * Only one, since cdk `dbe40bd2` removed the confirm-the-number step: a phone the applicant has
 * already *verified* receives a code immediately and answers `CODE_SENT`, so `NEEDS_PHONE` is the
 * sole reason to ask for a number.
 *
 * "Verified", not merely submitted — this used to read "bound", which cost a release cycle.
 * `startOnboardingSession` reads the server's `boundPhone`, and only a successful
 * `verifyOnboardingCode` writes it; `bindOnboardingPhone` writes `pendingPhone`. A draft sitting
 * between the two answers `NEEDS_PHONE`, which is why the code screen resends via
 * `bindOnboardingPhone` rather than through the session start. The status therefore conflates
 * "never gave us a number" with "gave us one, has not proved it yet"; the client tells them apart
 * by whether it still holds the number in `session.pendingPhone`.
 *
 * Kept as a list because the gate in `OnBoard.tsx` reads it alongside `CODE_SCREEN_STATUSES`, and a
 * lone `===` there would hide which of the two sets a future status belongs in.
 */
export const PHONE_SCREEN_STATUSES = [NEEDS_PHONE];

/**
 * Statuses that mean "the code screen is where you belong".
 *
 * The three refusals are here for the same reason as `CODE_SENT`: they are refusals to send
 * *another* code, not refusals of the caller, so reaching any of them means the invitation code
 * resolved and a number is already bound. Treating them as terminal strands someone who is one step
 * from finishing — which is what happened before commit 613844378 added `TOO_SOON`.
 *
 * `CODE_STILL_VALID` is the strongest case of all and was missed when the server split it out of
 * `TOO_SOON`: it means a live code is in the applicant's hand right now. Landing them anywhere but
 * the code screen makes that code unusable, and the invitation screen's generic failure told them
 * their code was invalid while a valid SMS sat on their phone.
 *
 * `TOO_SOON` and `HOURLY_CAP_REACHED` route here even though no code is live, because the code
 * screen is the only place that shows the reason, the countdown, and a resend.
 */
export const CODE_SCREEN_STATUSES = [CODE_SENT, CODE_STILL_VALID, TOO_SOON, HOURLY_CAP_REACHED];
